import axios, {AxiosHeaders, CanceledError, HttpStatusCode} from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import EntityOfficerInvitation from "legal-entities/entities/entity-officer-invitation/entity-officer-invitation";
import DisableUserAPIRequest from "users/entities/super-user/api/request-contracts/disable-user-api-request";
import EnableUserAPIRequest from "users/entities/super-user/api/request-contracts/enable-user-api-request";
import InviteEntityOfficerAPIRequest
  from "users/entities/super-user/api/request-contracts/invite-entity-officer-api-request";
import LoginAsUserAPIRequest from "users/entities/super-user/api/request-contracts/login-as-user-api-request";
import RegisterAPIRequest from "users/entities/super-user/api/request-contracts/register-api-request";
import SearchUsersAPIRequest from "users/entities/super-user/api/request-contracts/search-users-api-request";
import UnregisterAPIRequest from "users/entities/super-user/api/request-contracts/unregister-api-request";
import PagedUsersAPIResponse from "users/entities/super-user/api/response-contracts/paged-users-api-response";
import SystemNotificationAPIResponse
  from "users/entities/super-user/api/response-contracts/system-notification-api-response";
import SystemNotification from "users/entities/system-notification/system-notification";
import {AuthResponse} from "users/entities/user/api/auth-email-api-service";
import AuthInfoAPIResponse from "users/entities/user/api/response-contracts/auth-info-api-response";
import User from "users/entities/user/user";
import Session from "users/session/session";
import PendingUserEntityMemberInvitationAPIResponse
  from "users/values/pending-user-entity-member-invitation/api/pending-user-entity-member-invitation-a-p-i-response";
import PendingUserEntityMemberInvitation
  from "users/values/pending-user-entity-member-invitation/pending-user-entity-member-invitation";

export default class SuperUserAPIService {

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private headerWithJson(): AxiosHeaders {
    return this.authHeaders.concat(
      { "Content-Type": "application/json" }
    );
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a SuperUserAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async register(user: User): Promise<void> {
    const url = new URL(
      "/super-user/register",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        new RegisterAPIRequest(user),
        {
          headers: this.headerWithJson()
        }
      );

    } catch (error) {
      throw new SuperUserAPIServiceError("register", error);
    }
  }

  async unregister(user: User): Promise<void> {
    const url = new URL(
      "/super-user/unregister",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        new UnregisterAPIRequest(user),
        { headers: this.headerWithJson() }
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("unregister", error);
    }
  }

  async disableUser(user: User): Promise<void> {
    const url = new URL(
      "/super-user/disable-user",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        new DisableUserAPIRequest(user),
        { headers: this.headerWithJson() }
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("disableUser", error);
    }
  }

  async enableUser(user: User): Promise<void> {
    const url = new URL(
      "/super-user/enable-user",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        new EnableUserAPIRequest(user),
        { headers: this.headerWithJson() }
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("enableUser", error);
    }
  }

  async loginAsUser(userToSpoof: User): Promise<AuthResponse> {
    const url = new URL(
      "/super-user/login-as-user",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        new LoginAsUserAPIRequest(userToSpoof),
        { headers: this.headerWithJson() }
      );

      const authInfo: AuthInfoAPIResponse = Object.assign(
        new AuthInfoAPIResponse(),
        response.data);
      const user = authInfo.deserializeUser();
      const context = authInfo.deserializeContext();
      const authToken = authInfo.deserializeAuthToken();
      const legalEntities = authInfo.deserializeUserEntityInfo();
      const openEntityAgreements = authInfo.deserializeOpenEntityAgreements();
      const networkConnections = authInfo.deserializeNetworkConnections();
      const networkInvitations = authInfo.deserializeNetworkInvitations();
      const bookmarkedIndividuals = authInfo.deserializeBookmarkedIndividuals();
      const bookmarkedCompanies = authInfo.deserializeBookmarkedCompanies();
      const bookmarkedTeams = authInfo.deserializeBookmarkedTeams();
      return {
        user, context, authToken, legalEntities,
        openEntityAgreements, networkConnections,
        networkInvitations, bookmarkedIndividuals,
        bookmarkedCompanies, bookmarkedTeams
      };
    } catch (error) {
      throw new SuperUserAPIServiceError("loginAsUser", error);
    }
  }

  async inviteEntityOfficer(
    invitation: EntityOfficerInvitation):
    Promise<EntityOfficerInvitation> {
    const url = new URL(
      "/super-user/invite-entity-officer",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        new InviteEntityOfficerAPIRequest(invitation),
        { headers: this.headerWithJson() }
      );
      invitation.id = new Guid(response.data);
      return invitation;
    }
    catch (error) {
      throw new SuperUserAPIServiceError("inviteEntityOfficer", error);
    }
  }

  async createSystemNotification(
    notificationType: string,
    notificationContent: string
  ): Promise<void> {
    const url = new URL(
      "/super-user/system-notifications",
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.post(
        url.toString(),
        { content: `${notificationContent}:${notificationType}` },
        { headers: this.headerWithJson() }
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("createSystemNotification", error);
    }
  }

  async deleteSystemNotification(notificationId: Guid) {
    const url = new URL(
      `/super-user/system-notifications/${notificationId}`,
      AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(
        url.toString(),
        { headers: this.authHeaders }
      );
    } catch (error) {
      throw new SuperUserAPIServiceError("deleteSystemNotification", error);
    }
  }

  async searchUsers(request: SearchUsersAPIRequest, abortController?: AbortController): Promise<PagedUsersAPIResponse> {
    try {
      const url = request.toUrl("/super-user/search-users", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return new PagedUsersAPIResponse(response.data);
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      throw new SuperUserAPIServiceError("searchUsers", error);
    }
  }

  async getAppVersion(abortController?: AbortController): Promise<string> {
    const url = new URL("/super-user/version", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden) {
        throw new VersionInfoNotAvailableError(
          "Unable to fetch version info, caller must be a super-user"
        );
      }
      if (error instanceof CanceledError)
        throw error;
      throw new SuperUserAPIServiceError("getAppVersion", error);
    }
  }

  async getSystemNotifications(abortController?: AbortController): Promise<SystemNotification[]> {
    const url = new URL("/super-user/system-notifications", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        });
      const systemNotifications: SystemNotification[] = [];
      response.data.forEach((item: Partial<SystemNotificationAPIResponse>) => {
        const systemNotification = new SystemNotificationAPIResponse(item);
        systemNotifications.push(systemNotification.deserialize());
      });
      return systemNotifications;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.Forbidden) {
        throw new SystemNotificationsNotFoundError();
      }
      if (error instanceof CanceledError)
        throw error;
      throw new SuperUserAPIServiceError("getSystemNotifications", error);
    }
  }

  async getActiveUserInvitations(abortController?: AbortController): Promise<PendingUserEntityMemberInvitation[]> {
    const url = new URL("/super-user/active-user-invitations", AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        });
      const invitations: PendingUserEntityMemberInvitation[] = [];
      response.data.forEach((item: Partial<PendingUserEntityMemberInvitationAPIResponse>) => {
        invitations.push(Object.assign(new PendingUserEntityMemberInvitationAPIResponse(), item).deserialize());
      });
      return invitations;
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound) {
        throw new UserEntityMemberInvitationsNotFoundError();
      }
      if (error instanceof CanceledError)
        throw error;
      throw new SuperUserAPIServiceError("getActiveUserInvitations", error);
    }
  }
}

export class SuperUserAPIServiceError extends Error {
  constructor(method: string, error: unknown) {
    super(`Error in SuperUserAPIService.${method}: ${error}`);
  }
}
export class SystemNotificationsNotFoundError extends Error {
  constructor() {
    super(`Unable to fetch any system notifications`);
  }
}
export class UserEntityMemberInvitationsNotFoundError extends Error {
  constructor() {
    super(`Unable to retrieve open user entity member invitations`);
  }
}
export class VersionInfoNotAvailableError extends Error { }
